import React, { FunctionComponent, ReactNode, useState } from 'react';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import { Button, CircularProgress, Collapse, ListItemButton, ListItemIcon, ListItemText, Stack } from '@mui/material';

import { canStartTrial } from '@paragon/login-tools';
import { OptionalTooltip } from '@paragon/material';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import {
  isActiveComponentDesign,
  isViewingNonCommittedComponent,
} from '../../../store/selectors/viewOnlyModeSelectors';
import { useAppSelector } from '../../hooks';
import { useDownloadContext } from './DownloadListItems';

export type SpecificDownloadTarget = 'any' | 'whole-layout' | 'whole-project' | 'single-component' | 'custom';

export interface DownloadListItemProps {
  readonly visible?: boolean /** defaults to true */;
  readonly description: string;
  readonly disabled?: boolean;

  /**
   * Allows this item to be shown only for specific download targets.
   */
  readonly for: readonly SpecificDownloadTarget[];

  /**
   * If both `disabled` and `upsell` are true, a tooltip will be shown to upsell the user to a paid plan.
   */
  readonly upsell?: boolean;

  /**
   * If provided while upsell mode is not active, this message will be shown in a tooltip for disabled items to explain
   *   why they are not available.
   *
   * Note that this is based on the `disabled` prop only; tooltips are not shown for items disabled by the universal
   *   checks for invalid state (preventing a single component download when there is no active component design, for
   *   example).
   */
  readonly alternateTooltipExplainingDisabledState?: string;

  readonly onClick: () => Promise<void> | void;

  readonly children?: ReactNode;
}

export const DownloadListItem: FunctionComponent<DownloadListItemProps> = (props) => {
  const context = useDownloadContext();

  const trialAvailable = useAppSelector(canStartTrial);
  const activeComponentDesignExists = useAppSelector(isActiveComponentDesign);
  const viewingNonCommittedComponent = useAppSelector(isViewingNonCommittedComponent);

  const [downloadOngoing, setDownloadOngoing] = useState<boolean>(false);
  const [optionsOpen, setOptionsOpen] = useState<boolean>(false);

  if (props.visible === false) return null;

  switch (context.mode) {
    case 'bulk':
      if (!props.for.includes('any') && !props.for.includes('whole-layout') && !props.for.includes('whole-project'))
        return null;
      else break;

    case 'custom':
      if (!props.for.includes('any') && !props.for.includes('custom')) return null;
      else break;

    case 'single':
      if (!props.for.includes('any') && !props.for.includes('single-component')) return null;
      else break;

    default:
      assertExhaustiveSwitch(context.mode);
  }

  const disabled =
    props.disabled ||
    downloadOngoing ||
    (context.mode === 'single' && (!activeComponentDesignExists || viewingNonCommittedComponent));

  const shouldUpsellInTooltip = props.disabled && props.upsell === true;
  const tooltipMessage = shouldUpsellInTooltip
    ? trialAvailable
      ? 'Start your free trial to access'
      : 'Upgrade to access'
    : props.alternateTooltipExplainingDisabledState;

  async function onDownload() {
    if (disabled) return;
    setDownloadOngoing(true);

    if (context.onBeforeDownloadBegins) {
      context.onBeforeDownloadBegins();
    }

    await props.onClick();
    setDownloadOngoing(false);
  }

  return (
    <>
      <OptionalTooltip visible={disabled && tooltipMessage != null} title={tooltipMessage} placement="left">
        <ListItemButton
          disabled={disabled}
          key={props.description}
          sx={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => (props.children ? setOptionsOpen((oldState) => !oldState) : onDownload())}
        >
          {!props.children && (
            <ListItemIcon sx={{ minWidth: 40 }}>
              {downloadOngoing ? <CircularProgress size={32} /> : <DownloadIcon />}
            </ListItemIcon>
          )}
          <ListItemText primary={props.description} />
          {props.children ? optionsOpen ? <ExpandLess /> : <ExpandMore /> : null}
        </ListItemButton>
      </OptionalTooltip>
      {props.children ? (
        <Collapse in={optionsOpen}>
          <Stack sx={{ p: 1 }} spacing={1}>
            {props.children}
            <Button variant="outlined" onClick={onDownload}>
              Download {props.description}
            </Button>
          </Stack>
        </Collapse>
      ) : null}
    </>
  );
};
